import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useDoctorSearch, useInviteCollaborationDoctor } from '../hooks/useCollaborationQueries';

export function InviteDoctorDialog({ collaborationId, members, onClose }) {
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim());
  const doctors = useDoctorSearch({ q: search, mode: 'all' }, { enabled: search.length >= 2 });
  const invite = useInviteCollaborationDoctor();
  const existingIds = new Set(members.map((member) => String(member.doctor?.id)));
  const candidates = (doctors.data?.items || []).filter((doctor) => !existingIds.has(String(doctor.id)));

  const sendInvitation = async (doctor) => {
    try {
      await invite.mutateAsync({ collaborationId, doctorId: doctor.id });
      toast.success(`Invitation sent to ${doctor.name}.`);
      onClose();
    } catch (error) {
      toast.error(error?.message || 'Unable to invite this doctor.');
    }
  };

  return (
    <div className="collab-member-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="collab-member-dialog" role="dialog" aria-modal="true" aria-labelledby="collab-invite-title">
        <header>
          <h3 id="collab-invite-title">Invite a doctor</h3>
          <button type="button" className="collab-member-dialog__close" aria-label="Close invitation" onClick={onClose}><X size={18} /></button>
        </header>
        <p>Search for a colleague. They will receive an invitation and join after accepting.</p>
        <label className="collab-input"><Search size={16} /><input autoFocus aria-label="Search doctors to invite" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Doctor name or email" /></label>
        <div className="collab-member-dialog__results" aria-live="polite">
          {search.length < 2 && <p>Enter at least two characters to search.</p>}
          {search.length >= 2 && doctors.isFetching && <p>Searching doctors…</p>}
          {search.length >= 2 && doctors.isError && <p>Unable to search doctors.</p>}
          {search.length >= 2 && !doctors.isFetching && !doctors.isError && candidates.length === 0 && <p>No eligible doctors found.</p>}
          {search.length >= 2 && !doctors.isFetching && candidates.map((doctor) => (
            <button type="button" key={doctor.id} disabled={invite.isPending} onClick={() => sendInvitation(doctor)}>
              <span><strong>{doctor.name}</strong><small>{doctor.specialty || 'General medicine'}</small></span>
              <span>{invite.isPending ? 'Sending…' : 'Invite'}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export default InviteDoctorDialog;
